/**
 * Phone number normalization & validation.
 * ----------------------------------------
 * Every `tel:` and WhatsApp link in the app must be built from a strictly
 * normalized E.164 number (no spaces, no punctuation, leading `+`). This
 * module is the single source of truth so a badly formatted constant or a
 * user-provided value can never leak into the DOM as a broken link.
 */

/** A phone number normalized to E.164 (e.g. "+243898333056"). */
export type E164 = string;

/**
 * Strips every character that is not a digit and normalizes international
 * prefixes ("00" → "+"). Returns null when the result cannot be a valid
 * international number.
 */
export const normalizePhone = (raw: string | null | undefined): E164 | null => {
  if (!raw) return null;

  let value = String(raw).trim();
  // Remove common separators: spaces, dots, dashes, slashes, parentheses,
  // non-breaking spaces, and the tel:/whatsapp URL wrappers.
  value = value.replace(/^tel:/i, '').replace(/^https?:\/\/wa\.me\//i, '');
  const hadPlus = value.trimStart().startsWith('+');
  const digits = value.replace(/\D/g, '');
  if (!digits) return null;

  let national = digits;
  if (!hadPlus && national.startsWith('00')) national = national.slice(2);

  // E.164: 8 to 15 digits, first digit cannot be 0.
  if (national.length < 8 || national.length > 15) return null;
  if (national.startsWith('0')) return null;

  return `+${national}`;
};

/** True when the value can be normalized to a valid international number. */
export const isValidPhone = (raw: string | null | undefined): boolean =>
  normalizePhone(raw) !== null;

/** Builds a safe `tel:` href, or null when the number is invalid. */
export const telHref = (raw: string | null | undefined): string | null => {
  const e164 = normalizePhone(raw);
  return e164 ? `tel:${e164}` : null;
};

/**
 * Builds a safe `https://wa.me/<digits>` link (wa.me requires the number
 * without the leading `+`). An optional prefilled message is encoded.
 */
export const whatsappHref = (
  raw: string | null | undefined,
  message?: string,
): string | null => {
  const e164 = normalizePhone(raw);
  if (!e164) return null;
  const base = `https://wa.me/${e164.slice(1)}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
};

/**
 * Human-readable display for DRC numbers: "+243 898 333 056".
 * Falls back to grouping by 3 for other country codes, and returns the raw
 * input untouched when it cannot be normalized (never throws).
 */
export const formatPhoneDisplay = (raw: string | null | undefined): string => {
  const e164 = normalizePhone(raw);
  if (!e164) return (raw ?? '').trim();

  const digits = e164.slice(1);
  if (digits.startsWith('243')) {
    const rest = digits.slice(3);
    const groups = rest.match(/\d{1,3}/g) ?? [];
    return `+243 ${groups.join(' ')}`.trim();
  }
  return `+${digits.replace(/(\d{3})(?=\d)/g, '$1 ')}`;
};
